/**
 * Plánovač: pro zadané A, B, čas a vybrané režimy spočítá množinu
 * kandidátních tras (itinerářů) se všemi metrikami.
 *
 * Jádro je čistě synchronní a deterministické. Reálné geometrie tras
 * (z routovací služby) se předávají zvenčí přes GeometryProvider —
 * když chybí, použije se transparentně označený odhad
 * (vzdušná čára × koeficient nepřímosti, prohnutá křivka).
 */
import { estimatedArc, haversineKm } from './geo';
import { findJourney, rideGeometry, VEHICLE_ICON } from './transit';
import type {
  Itinerary,
  LatLng,
  ModeProfile,
  NamedPoint,
  PlanRequest,
  PlanResult,
  PlanTuning,
  RoutingProfile,
  Segment,
  InfeasibleResult,
  TransitNetwork,
} from './types';

export interface RouteGeometry {
  geometry: LatLng[];
  distanceKm: number;
  real: boolean;
  /** Doba jízdy dle routovací služby (min); null/undefined = neposkytnuta. */
  durationMin?: number | null;
  /** Id provideru, který trasu dodal (osrm, ors…). */
  source?: string;
}

/** Dodavatel geometrie trasy; undefined = použij odhad. */
export type GeometryProvider = (
  profile: RoutingProfile,
  from: LatLng,
  to: LatLng,
) => RouteGeometry | undefined;

const MIN_TRIP_KM = 0.02; // ~20 m — pod tím A≈B

/** Výchozí pěší profil pro výstupy z MHD (když je režim Pěšky vypnutý). */
const FALLBACK_WALK = { speedKmh: 4.8, circuity: 1.25 };

export function plan(req: PlanRequest, geo?: GeometryProvider): PlanResult {
  const { origin, destination, departMs, modes, transit } = req;
  const warnings: string[] = [];
  const itineraries: Itinerary[] = [];
  const infeasible: InfeasibleResult[] = [];
  /** Trasy porušující okno + popis porušení (kandidáti „nejbližšího možného"). */
  const nearPool: Array<{ it: Itinerary; note: string; deltaMin: number }> = [];

  const crowKm = haversineKm(origin, destination);
  if (crowKm < MIN_TRIP_KM) {
    warnings.push('Body A a B jsou prakticky totožné — není co plánovat.');
    return { itineraries, infeasible, nearest: [], crowKm, warnings };
  }

  const enabled = modes.filter((m) => m.enabled);
  if (enabled.length === 0) {
    warnings.push('Není vybrán žádný dopravní režim. Zapněte alespoň jeden.');
    return { itineraries, infeasible, nearest: [], crowKm, warnings };
  }

  // Časová okna: vše je interval; chybějící mez = bez omezení.
  const W = req.windows ?? null;
  const depFloor = W?.depFromMs ?? departMs;
  const depCeil = W?.depToMs ?? null;
  const arrFloor = W?.arrFromMs ?? null;
  const arrCeil = W?.arrToMs ?? null;

  // Strategie „odjet co nejpozději" potřebuje strop příjezdu.
  let strategy = req.strategy ?? 'default';
  if (strategy === 'latest' && arrCeil === null) {
    warnings.push('Pro „nejpozdější odjezd" zadejte okno příjezdu (do kdy chcete dorazit). Zatím hledám běžně.');
    strategy = 'default';
  }

  /** Vyhodnotí okna; vyhovující → itineraries, jinak infeasible + nearPool. */
  const accept = (it: Itinerary, mode: ModeProfile): void => {
    let note: string | null = null;
    let delta = 0;
    if (arrCeil !== null && it.arrivalMs > arrCeil) {
      delta = Math.round((it.arrivalMs - arrCeil) / 60_000);
      note = `Příjezd ${fmtDelta(it.arrivalMs - arrCeil)} po konci okna příjezdu.`;
    } else if (arrFloor !== null && it.arrivalMs < arrFloor) {
      delta = Math.round((arrFloor - it.arrivalMs) / 60_000);
      note = `Příjezd ${fmtDelta(arrFloor - it.arrivalMs)} před začátkem okna příjezdu.`;
    } else if (depCeil !== null && it.departureMs > depCeil) {
      delta = Math.round((it.departureMs - depCeil) / 60_000);
      note = `Odjezd ${fmtDelta(it.departureMs - depCeil)} po konci okna odjezdu.`;
    }
    if (note) {
      infeasible.push({ modeId: it.modeId, name: it.name, icon: it.icon, reason: note });
      nearPool.push({ it: { ...it, windowNote: note }, note, deltaMin: delta });
    } else {
      itineraries.push(it);
    }
  };

  const walkMode = modes.find((m) => m.id === 'walk');
  const walkParams = walkMode
    ? { speedKmh: walkMode.speedKmh, circuity: walkMode.circuity }
    : FALLBACK_WALK;

  for (const mode of enabled) {
    if (mode.kind === 'pt' || mode.kind === 'combined') {
      const access = mode.kind === 'combined' ? modes.find((m) => m.id === mode.accessModeId) : null;
      if (mode.kind === 'combined' && !access) {
        infeasible.push({ modeId: mode.id, name: mode.name, icon: mode.icon, reason: 'Chybí návazný režim pro první úsek.' });
        continue;
      }
      // Start hledání = spodek okna odjezdu; když přijedeme před oknem
      // příjezdu, posuneme start a hledáme znovu (max 4 iterace).
      let t0 = depFloor;
      let it: Itinerary | null = null;
      for (let i = 0; i < 4; i++) {
        it = planTransit({ ...req, departMs: t0 }, mode, walkParams, access ?? null, geo);
        if (!it || arrFloor === null || it.arrivalMs >= arrFloor) break;
        t0 += arrFloor - it.arrivalMs;
        if (depCeil !== null && t0 > depCeil) break;
      }
      // „Odjet co nejpozději": binární hledání nejpozdějšího startu,
      // se kterým ještě stihneme strop příjezdu (findJourney je rychlý).
      if (it && strategy === 'latest' && arrCeil !== null && it.arrivalMs <= arrCeil) {
        let lo = t0; // vyhovuje
        let hi = Math.min(arrCeil, depCeil ?? arrCeil); // nad strop nemá smysl
        let best = it;
        for (let i = 0; i < 12 && hi - lo > 60_000; i++) {
          const mid = lo + Math.floor((hi - lo) / 2);
          const cand = planTransit({ ...req, departMs: mid }, mode, walkParams, access ?? null, geo);
          if (cand && cand.arrivalMs <= arrCeil) {
            best = cand;
            lo = mid;
          } else {
            hi = mid;
          }
        }
        it = best;
      }
      if (it) accept(it, mode);
      else
        infeasible.push({
          modeId: mode.id,
          name: mode.name,
          icon: mode.icon,
          reason:
            transit.lines.length === 0
              ? mode.kind === 'pt'
                ? 'Pro tuto oblast není k dispozici žádná linka MHD.'
                : 'Kombinace s MHD tady nedává smysl (žádné vhodné spojení).'
              : 'Nenašlo se žádné vhodné spojení (zastávky jsou moc daleko, nebo nic nejede).',
        });
      continue;
    }
    // Přímé režimy (pěšky, kolo, auto, vlastní…): odjezd lze v okně posouvat.
    const probe = planDirect(origin, destination, depFloor, mode, crowKm, geo, req.tuning);
    if ('reason' in probe) {
      infeasible.push(probe);
      continue;
    }
    let dep = depFloor;
    const durMs = probe.totalMin * 60_000;
    if (strategy === 'latest' && arrCeil !== null) {
      // Odjet co nejpozději: příjezd těsně na strop okna.
      dep = Math.max(depFloor, arrCeil - durMs);
    } else if (arrFloor !== null && dep + durMs < arrFloor) {
      // Přijet na začátek okna příjezdu → odjezd dopočítat (neodjíždět zbytečně brzy).
      dep = arrFloor - durMs;
    }
    if (depCeil !== null && dep > depCeil) dep = depCeil;
    const res = dep === depFloor ? probe : planDirect(origin, destination, dep, mode, crowKm, geo, req.tuning);
    if ('reason' in res) infeasible.push(res);
    else accept(res, mode);
  }

  // Filtr nejzazšího příjezdu — pozdní varianty přesouváme s vysvětlením.
  if (req.arriveByMs !== undefined) {
    for (let i = itineraries.length - 1; i >= 0; i--) {
      const it = itineraries[i];
      if (it.arrivalMs > req.arriveByMs) {
        infeasible.push({
          modeId: it.modeId,
          name: it.name,
          icon: it.icon,
          reason: `Příjezd ${fmtDelta(it.arrivalMs - req.arriveByMs)} po požadovaném limitu.`,
        });
        itineraries.splice(i, 1);
      }
    }
  }

  // Nic nevyhovuje oknům → explicitně to řekneme a nabídneme nejbližší možné.
  let nearest: Itinerary[] = [];
  if (itineraries.length === 0 && nearPool.length > 0) {
    nearest = nearPool
      .sort((a, b) => a.deltaMin - b.deltaMin)
      .map((n) => n.it);
    warnings.push(
      'Žádná trasa nesplňuje zadaná časová okna. Níže jsou nejbližší možné trasy — u každé je uvedeno, o kolik okno míjí.',
    );
  } else if (itineraries.length === 0 && infeasible.length > 0) {
    warnings.push('Žádný vybraný režim nenabídl použitelnou trasu. Zkuste upravit výběr nebo čas.');
  }

  return { itineraries, infeasible, nearest, crowKm, warnings };
}

function fmtDelta(ms: number): string {
  const min = Math.round(ms / 60_000);
  return min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`;
}

/** Popisek původu jízdního řádu pro segmenty MHD. */
function transitSourceLabel(source: TransitNetwork['source']): string {
  if (source === 'demo') return 'demo jízdní řád (odhad)';
  if (source === 'gtfs') return 'jízdní řád GTFS';
  return 'ruční jízdní řád';
}

/* ------------------------------------------------------------------ */
/* Přímé režimy                                                        */
/* ------------------------------------------------------------------ */

function planDirect(
  origin: NamedPoint,
  destination: NamedPoint,
  departMs: number,
  mode: ModeProfile,
  crowKm: number,
  geo?: GeometryProvider,
  tuning?: PlanTuning,
): Itinerary | InfeasibleResult {
  if (mode.maxDistanceKm !== undefined && crowKm > mode.maxDistanceKm) {
    return {
      modeId: mode.id,
      name: mode.name,
      icon: mode.icon,
      reason: `Na ${crowKm.toFixed(1)} km tento režim nedává smysl (limit ${mode.maxDistanceKm} km).`,
    };
  }
  if (mode.minDistanceKm !== undefined && crowKm < mode.minDistanceKm) {
    return {
      modeId: mode.id,
      name: mode.name,
      icon: mode.icon,
      reason: 'Na tak krátkou vzdálenost se tento režim nevyplatí.',
    };
  }
  if (mode.speedKmh <= 0) {
    return {
      modeId: mode.id,
      name: mode.name,
      icon: mode.icon,
      reason: 'Režim má nulovou rychlost — upravte parametry.',
    };
  }

  const routed = mode.routingProfile ? geo?.(mode.routingProfile, origin, destination) : undefined;
  const distanceKm = routed ? routed.distanceKm : crowKm * mode.circuity;
  const geometry = routed ? routed.geometry : estimatedArc(origin, destination);
  const real = routed?.real ?? false;

  // Doba jízdy: u aut přednostně z routovací služby (zohledňuje typ silnic);
  // u osobních režimů (pěšky/kolo/…) z uživatelské rychlosti nad reálnou délkou.
  const isCarLike = mode.routingProfile === 'car';
  const providerMin =
    isCarLike && tuning?.useProviderDuration !== false && routed?.durationMin
      ? routed.durationMin
      : null;
  let rideMin = providerMin ?? (distanceKm / mode.speedKmh) * 60;
  // Model dopravní špičky: prodlužuje jízdu autem v čase odjezdu.
  const carFactor = isCarLike ? Math.max(1, tuning?.carTimeFactor ?? 1) : 1;
  rideMin *= carFactor;
  const totalMin = mode.setupMin + mode.waitMin + rideMin + mode.teardownMin;
  const cost = mode.costFixed + mode.costPerKm * distanceKm;
  const emissions = mode.emissionsPerKm * distanceKm;
  const rideNote =
    providerMin !== null
      ? carFactor > 1.01
        ? `Čas z routovací služby × ${carFactor.toFixed(2)} (model špičky)`
        : 'Čas jízdy z routovací služby'
      : carFactor > 1.01
        ? `Model špičky: ×${carFactor.toFixed(2)}`
        : undefined;

  const segments: Segment[] = [];
  let t = 0;
  const pushSeg = (s: Omit<Segment, 'color' | 'icon'>): void => {
    segments.push({ ...s, color: mode.color, icon: mode.icon });
  };
  if (mode.setupMin > 0) {
    pushSeg({
      kind: 'setup',
      refId: mode.id,
      label: 'Příprava',
      from: origin,
      to: origin,
      startMin: t,
      endMin: (t += mode.setupMin),
      distanceKm: 0,
      geometry: [],
      realGeometry: false,
      cost: 0,
      emissions: 0,
      note: 'Fixní zdržení na startu',
    });
  }
  if (mode.waitMin > 0) {
    pushSeg({
      kind: 'wait',
      refId: mode.id,
      label: 'Čekání',
      from: origin,
      to: origin,
      startMin: t,
      endMin: (t += mode.waitMin),
      distanceKm: 0,
      geometry: [],
      realGeometry: false,
      cost: 0,
      emissions: 0,
    });
  }
  pushSeg({
    kind: 'ride',
    refId: mode.id,
    label: mode.name,
    from: origin,
    to: destination,
    startMin: t,
    endMin: (t += rideMin),
    distanceKm,
    geometry,
    realGeometry: real,
    cost,
    emissions,
    note: rideNote,
  });
  if (mode.teardownMin > 0) {
    pushSeg({
      kind: 'teardown',
      refId: mode.id,
      label: 'Dokončení',
      from: destination,
      to: destination,
      startMin: t,
      endMin: (t += mode.teardownMin),
      distanceKm: 0,
      geometry: [],
      realGeometry: false,
      cost: 0,
      emissions: 0,
      note: 'Parkování / zamknutí / přezutí',
    });
  }

  const uncertaintyMin = estimateUncertainty(totalMin, mode.reliability, real);

  return {
    id: `it-${mode.id}`,
    modeId: mode.id,
    name: mode.name,
    icon: mode.icon,
    color: mode.color,
    departureMs: departMs,
    arrivalMs: departMs + totalMin * 60_000,
    totalMin,
    distanceKm,
    waitMin: mode.waitMin,
    transfers: 0,
    cost,
    emissions,
    comfort: mode.comfort,
    reliability: mode.reliability,
    uncertaintyMin,
    segments,
    // real = geometrie i čas ze služby; mixed = reálná trasa + modelový čas.
    dataSource: real ? (providerMin !== null ? 'real' : 'mixed') : 'estimate',
    badges: [],
    accessible: mode.accessible,
    carFree: mode.carFree,
    why: directWhy(mode),
  };
}

function directWhy(mode: ModeProfile): string {
  switch (mode.kind) {
    case 'walk':
      return 'Žádné čekání, nulové náklady a emise. Tempo si řídíte sami.';
    case 'bike':
      return 'Rychlé ve městě, levné a nezávislé na jízdním řádu.';
    case 'micro':
      return 'Flexibilní na krátké vzdálenosti, bez vlastního vybavení.';
    case 'skate':
      return 'Sportovní varianta — záleží na povrchu a počasí.';
    case 'car':
      return 'Nejvyšší komfort od dveří ke dveřím; počítejte s parkováním.';
    default:
      return 'Vlastní režim podle vašich parametrů.';
  }
}

/** Nejistota: nespolehlivost režimu škáluje s délkou cesty; odhad bez reálné geometrie přidává. */
export function estimateUncertainty(totalMin: number, reliability: number, real: boolean): number {
  const base = totalMin * (1 - reliability) * 0.8;
  const estimatePenalty = real ? 0 : totalMin * 0.08;
  return Math.round((base + estimatePenalty) * 10) / 10;
}

/* ------------------------------------------------------------------ */
/* Veřejná doprava a kombinace                                          */
/* ------------------------------------------------------------------ */

function planTransit(
  req: PlanRequest,
  mode: ModeProfile,
  walk: { speedKmh: number; circuity: number },
  accessMode: ModeProfile | null,
  geo?: GeometryProvider,
): Itinerary | null {
  const { origin, destination, departMs, transit } = req;

  const accessSpeed = accessMode ? accessMode.speedKmh : walk.speedKmh;
  const accessCircuity = accessMode ? accessMode.circuity : walk.circuity;
  // Kombinace: na zastávku smí být dál (kolo/auto dojede), pěšky držíme limit sítě.
  const maxAccessKm = accessMode
    ? accessMode.id === 'car'
      ? Math.max(transit.maxAccessKm, 15)
      : Math.max(transit.maxAccessKm, 5)
    : transit.maxAccessKm;

  // Fixní zdržení přístupového režimu posune reálný odjezd.
  const accessOverheadMin = accessMode
    ? accessMode.setupMin + accessMode.waitMin + accessMode.teardownMin
    : 0;
  const searchStartMs = departMs + accessOverheadMin * 60_000;

  const j = findJourney(origin, destination, searchStartMs, transit, {
    accessSpeedKmh: accessSpeed,
    accessCircuity,
    walkSpeedKmh: walk.speedKmh,
    walkCircuity: walk.circuity,
    maxAccessKm,
    maxTransfers: req.tuning?.maxTransfers,
  });
  if (!j) return null;

  // Kombinovaná varianta musí MHD reálně využít víc než triviálně.
  if (accessMode && j.rideKm < 0.5) return null;

  const segments: Segment[] = [];
  let waitMin = j.waitMin;
  let distanceKm = 0;
  let cost = 0;
  let emissions = 0;

  const toMin = (ms: number): number => (ms - departMs) / 60_000;

  // Případná příprava přístupového režimu (odemknutí kola, nastartování…).
  if (accessMode && accessMode.setupMin + accessMode.waitMin > 0) {
    segments.push({
      kind: 'setup',
      refId: accessMode.id,
      label: `Příprava (${accessMode.name.toLowerCase()})`,
      from: origin,
      to: origin,
      startMin: 0,
      endMin: accessMode.setupMin + accessMode.waitMin,
      distanceKm: 0,
      geometry: [],
      realGeometry: false,
      cost: 0,
      emissions: 0,
      color: accessMode.color,
      icon: accessMode.icon,
    });
  }

  for (const leg of j.legs) {
    if (leg.kind === 'access') {
      const am = accessMode ?? null;
      const profile: RoutingProfile = am
        ? am.routingProfile ?? 'foot'
        : 'foot';
      const target = { lat: leg.toStop!.lat, lng: leg.toStop!.lng, name: leg.toStop!.name };
      const routed = geo?.(profile, origin, target);
      const km = routed ? routed.distanceKm : leg.distanceKm * accessCircuity;
      const legCost = am ? am.costFixed + am.costPerKm * km : 0;
      const legEmis = am ? am.emissionsPerKm * km : 0;
      distanceKm += km;
      cost += legCost;
      emissions += legEmis;
      segments.push({
        kind: am ? 'ride' : 'walk',
        refId: am ? am.id : 'walk',
        label: am ? `${am.name} na zastávku ${target.name}` : `Pěšky na zastávku ${target.name}`,
        from: origin,
        to: target,
        startMin: toMin(leg.startMs),
        endMin: toMin(leg.endMs),
        distanceKm: km,
        geometry: routed ? routed.geometry : estimatedArc(origin, target),
        realGeometry: routed?.real ?? false,
        cost: legCost,
        emissions: legEmis,
        color: am ? am.color : '#22a06b',
        icon: am ? am.icon : 'walk',
        note: am ? 'Dopravní prostředek necháte u zastávky' : undefined,
      });
      // Dokončení přístupového režimu (zamknutí kola / zaparkování) je v transferMin sítě.
    } else if (leg.kind === 'wait') {
      const at = leg.fromStop!;
      segments.push({
        kind: 'wait',
        refId: leg.line?.id ?? mode.id,
        label: `Čekání na ${leg.line ? `linku ${leg.line.name}` : 'spoj'}`,
        from: { lat: at.lat, lng: at.lng, name: at.name },
        to: { lat: at.lat, lng: at.lng, name: at.name },
        startMin: toMin(leg.startMs),
        endMin: toMin(leg.endMs),
        distanceKm: 0,
        geometry: [],
        realGeometry: false,
        cost: 0,
        emissions: 0,
        color: '#94a3b8',
        icon: 'wait',
        note: `Zastávka ${at.name}`,
      });
    } else if (leg.kind === 'ride') {
      const line = leg.line!;
      const from = leg.fromStop!;
      const to = leg.toStop!;
      const legEmis = line.emissionsPerKm * leg.distanceKm;
      emissions += legEmis;
      distanceKm += leg.distanceKm;
      segments.push({
        kind: 'transit',
        refId: line.id,
        label: `Linka ${line.name}: ${from.name} → ${to.name}`,
        from: { lat: from.lat, lng: from.lng, name: from.name },
        to: { lat: to.lat, lng: to.lng, name: to.name },
        startMin: toMin(leg.startMs),
        endMin: toMin(leg.endMs),
        distanceKm: leg.distanceKm,
        geometry: rideGeometry(leg.viaStops ?? [from, to]),
        realGeometry: transit.source !== 'demo',
        cost: 0, // jízdné řešíme jednou jízdenkou níže
        emissions: legEmis,
        color: line.color,
        icon: VEHICLE_ICON[line.vehicle] ?? 'bus',
        note: `${(leg.viaStops?.length ?? 2) - 1} mezizastávek · ${transitSourceLabel(transit.source)}`,
      });
    } else if (leg.kind === 'transfer') {
      const from = leg.fromStop!;
      const to = leg.toStop!;
      distanceKm += leg.distanceKm;
      segments.push({
        kind: 'walk',
        refId: 'walk',
        label: `Přestup pěšky: ${from.name} → ${to.name}`,
        from: { lat: from.lat, lng: from.lng, name: from.name },
        to: { lat: to.lat, lng: to.lng, name: to.name },
        startMin: toMin(leg.startMs),
        endMin: toMin(leg.endMs),
        distanceKm: leg.distanceKm,
        geometry: estimatedArc(from, to, 6),
        realGeometry: false,
        cost: 0,
        emissions: 0,
        color: '#22a06b',
        icon: 'walk',
      });
    } else {
      // egress
      const from = leg.fromStop!;
      const routed = geo?.('foot', from, destination);
      const km = routed ? routed.distanceKm : leg.distanceKm * walk.circuity;
      distanceKm += km;
      segments.push({
        kind: 'walk',
        refId: 'walk',
        label: `Pěšky do cíle`,
        from: { lat: from.lat, lng: from.lng, name: from.name },
        to: destination,
        startMin: toMin(leg.startMs),
        endMin: toMin(leg.endMs),
        distanceKm: km,
        geometry: routed ? routed.geometry : estimatedArc(from, destination),
        realGeometry: routed?.real ?? false,
        cost: 0,
        emissions: 0,
        color: '#22a06b',
        icon: 'walk',
      });
    }
  }

  // Jízdné: jedna jízdenka pokrývající všechny použité linky (nejdražší tarif).
  // Linky bez vlastního tarifu (např. GTFS import) → výchozí jízdné z parametrů.
  let fare = j.linesUsed.reduce((max, l) => Math.max(max, l.costFlat), 0);
  if (fare <= 0) fare = req.tuning?.defaultFareCzk ?? 0;
  cost += fare + (accessMode ? mode.costFixed : 0); // u P+R parkovné z profilu kombinace

  const arrivalMs = j.arriveMs;
  const totalMin = (arrivalMs - departMs) / 60_000;

  // Komfort: vážený průměr linek dle ujeté vzdálenosti.
  const rideLegs = j.legs.filter((l) => l.kind === 'ride');
  const comfortNum = rideLegs.reduce((s, l) => s + (l.line?.comfort ?? 3) * l.distanceKm, 0);
  const comfort = j.rideKm > 0 ? comfortNum / j.rideKm : mode.comfort;

  const reliability = Math.max(0.4, 0.88 * Math.pow(0.94, j.transfers)) * (accessMode ? 0.95 : 1);
  const uncertaintyMin =
    estimateUncertainty(totalMin, reliability, transit.source !== 'demo') +
    (transit.source === 'demo' ? totalMin * 0.05 : 0);

  const accessible =
    j.accessible && (accessMode ? accessMode.accessible : true);

  return {
    id: `it-${mode.id}`,
    modeId: mode.id,
    name: mode.name,
    icon: mode.icon,
    color: mode.color,
    departureMs: departMs,
    arrivalMs,
    totalMin,
    distanceKm,
    waitMin,
    transfers: j.transfers,
    cost,
    emissions,
    comfort: Math.round(comfort * 10) / 10,
    reliability: Math.round(reliability * 100) / 100,
    uncertaintyMin: Math.round(uncertaintyMin * 10) / 10,
    segments,
    // Demo síť = odhad; ruční/GTFS jízdní řád = reálná data + modelová geometrie.
    dataSource: transit.source === 'demo' ? 'estimate' : 'mixed',
    badges: [],
    accessible,
    carFree: accessMode ? accessMode.carFree : true,
    why: accessMode
      ? `Kombinace ${accessMode.name.toLowerCase()} + MHD: rychlý dojezd k páteřní lince bez hledání parkování v centru.`
      : 'Bez řízení a parkování; cena jedné jízdenky, předvídatelný čas podle jízdního řádu.',
  };
}
